/**
 * CommandPalette — Global Ctrl+K navigation & action layer
 *
 * Capabilities:
 *   - Smart Suggestions (SLA risk, escalation, guard, conversion momentum)
 *   - Navigate to pages (Dashboard, Leads, Messages, Settings, etc.)
 *   - Switch layout presets (Operations / Analytics / Executive)
 *   - Quick-filter leads by status
 *   - Inline lead/patient search (fuzzy match on participant names, subjects)
 *   - Fuzzy text search over actions
 *   - Recent actions (last 5, persisted in localStorage)
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, LayoutDashboard, Users, MessageCircle, FileText,
  Settings, BarChart3, LayoutGrid, Briefcase, Filter,
  ChevronRight, Clock, Command, Stethoscope, UserCog, UserCircle,
  AlertTriangle, Shield, TrendingDown, TrendingUp, Zap
} from 'lucide-react';
import api from '../utils/api';

const RECENT_KEY = 'treatx_command_palette_recent';
const MAX_RECENT = 5;
const MAX_LEAD_RESULTS = 10;
const DEBOUNCE_MS = 150;
const SLA_THRESHOLD_HOURS = 48;

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768;

// ── Action Registry ──────────────────────────────────────────────

const buildActions = ({ isAdmin }) => {
  const all = [
    { id: 'nav-dashboard', label: 'Go to Dashboard', group: 'Navigate', icon: LayoutDashboard, route: '/clinic/admin', roles: ['all'] },
    { id: 'nav-leads', label: 'Go to Leads', group: 'Navigate', icon: Users, route: '/clinic/admin', tab: 'leads', roles: ['all'] },
    { id: 'nav-messages', label: 'Go to Messages', group: 'Navigate', icon: MessageCircle, route: '/clinic/messages', roles: ['all'] },
    { id: 'nav-offers', label: 'Go to Offers', group: 'Navigate', icon: FileText, route: '/clinic/admin', tab: 'offers', roles: ['all'] },
    { id: 'nav-doctors', label: 'Go to Doctors', group: 'Navigate', icon: Stethoscope, route: '/clinic/admin', tab: 'doctors', roles: ['all'] },
    { id: 'nav-settings', label: 'Go to Settings', group: 'Navigate', icon: Settings, route: '/clinic/admin', tab: 'profile', roles: ['admin'] },
    { id: 'nav-users', label: 'Manage Users', group: 'Navigate', icon: UserCog, route: '/clinic/admin/users', roles: ['admin'] },
    { id: 'nav-subscription', label: 'Subscription & Packages', group: 'Navigate', icon: BarChart3, route: '/clinic/subscription', roles: ['admin'] },
    { id: 'preset-operations', label: 'Switch to Operations View', group: 'Layout', icon: LayoutGrid, preset: 'operations', roles: ['all'] },
    { id: 'preset-analytics', label: 'Switch to Analytics View', group: 'Layout', icon: BarChart3, preset: 'analytics', roles: ['all'] },
    { id: 'preset-executive', label: 'Switch to Executive View', group: 'Layout', icon: Briefcase, preset: 'executive', roles: ['all'] },
    { id: 'filter-new', label: 'Filter: New Leads', group: 'Filter', icon: Filter, tab: 'leads', filter: 'NEW', roles: ['all'] },
    { id: 'filter-contacted', label: 'Filter: Contacted', group: 'Filter', icon: Filter, tab: 'leads', filter: 'CONTACTED', roles: ['all'] },
    { id: 'filter-qualified', label: 'Filter: Qualified', group: 'Filter', icon: Filter, tab: 'leads', filter: 'QUALIFIED', roles: ['all'] },
    { id: 'filter-offer-sent', label: 'Filter: Offer Sent', group: 'Filter', icon: Filter, tab: 'leads', filter: 'OFFER_SENT', roles: ['all'] },
    { id: 'filter-won', label: 'Filter: Won Leads', group: 'Filter', icon: Filter, tab: 'leads', filter: 'WON', roles: ['all'] },
    { id: 'filter-lost', label: 'Filter: Lost Leads', group: 'Filter', icon: Filter, tab: 'leads', filter: 'LOST', roles: ['all'] },
  ];
  return all.filter(a => a.roles.includes('all') || (isAdmin && a.roles.includes('admin')));
};

// ── Smart Suggestions Engine ─────────────────────────────────────

const buildSuggestions = (threads, analyticsSummary, isAdmin, userId) => {
  const suggestions = [];
  const now = Date.now();
  const thresholdMs = SLA_THRESHOLD_HOURS * 60 * 60 * 1000;

  // Permission-filtered threads
  // B-3 / INV-A9 — canonical thread projection field is
  // `assigned_agent_id`. Legacy `assigned_to` retired.
  const visible = threads.filter(t => {
    if (isAdmin) return true;
    return !t.assigned_agent_id || t.assigned_agent_id === userId;
  });

  // 1. SLA Risk — leads created > 48h ago with no first_response_at
  const slaBreached = visible.filter(t => {
    if (t.lead_status === 'WON' || t.lead_status === 'LOST') return false;
    if (t.first_response_at) return false;
    const created = t.created_at ? new Date(t.created_at).getTime() : 0;
    return created > 0 && (now - created) > thresholdMs;
  });
  if (slaBreached.length > 0) {
    suggestions.push({
      id: 'suggest-sla-risk',
      icon: Clock,
      count: slaBreached.length,
      label: `${slaBreached.length} lead${slaBreached.length > 1 ? 's' : ''} not contacted in 48h`,
      color: 'text-amber-600',
      bg: 'bg-amber-50 border-amber-200',
      route: '/clinic/messages',
      action: 'sla-filter',
    });
  }

  // 2. Escalation Alerts — threads with escalated status
  const escalated = visible.filter(t => t.status === 'escalated');
  if (escalated.length > 0) {
    suggestions.push({
      id: 'suggest-escalation',
      icon: AlertTriangle,
      count: escalated.length,
      label: `${escalated.length} escalated thread${escalated.length > 1 ? 's' : ''} need review`,
      color: 'text-red-600',
      bg: 'bg-red-50 border-red-200',
      route: '/clinic/messages',
      action: 'escalation-filter',
    });
  }

  // 3. Guard Risk — threads with circumvention_flag
  const flagged = visible.filter(t => t.circumvention_flag === true);
  if (flagged.length > 0) {
    suggestions.push({
      id: 'suggest-guard-risk',
      icon: Shield,
      count: flagged.length,
      label: `${flagged.length} high-risk conversation${flagged.length > 1 ? 's' : ''} flagged`,
      color: 'text-orange-600',
      bg: 'bg-orange-50 border-orange-200',
      route: '/clinic/messages',
      action: 'guard-filter',
    });
  }

  // 4. Conversion Momentum — from pre-computed analytics summary
  const delta = analyticsSummary?.conversionDelta;
  if (delta !== null && delta !== undefined && delta !== 0) {
    const isDown = delta < 0;
    suggestions.push({
      id: 'suggest-conversion',
      icon: isDown ? TrendingDown : TrendingUp,
      count: Math.abs(delta),
      label: `Conversion rate ${isDown ? '↓' : '↑'}${Math.abs(delta)}% this week`,
      color: isDown ? 'text-red-600' : 'text-emerald-600',
      bg: isDown ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200',
      preset: 'analytics',
      action: 'view-analytics',
    });
  }

  // 5. Unassigned leads (admin only)
  if (isAdmin) {
    const unassigned = visible.filter(t =>
      !t.assigned_agent_id && t.lead_status && t.lead_status !== 'WON' && t.lead_status !== 'LOST'
    );
    if (unassigned.length > 0) {
      suggestions.push({
        id: 'suggest-unassigned',
        icon: Users,
        count: unassigned.length,
        label: `${unassigned.length} lead${unassigned.length > 1 ? 's' : ''} unassigned`,
        color: 'text-indigo-600',
        bg: 'bg-indigo-50 border-indigo-200',
        route: '/clinic/messages',
        action: 'unassigned-filter',
      });
    }
  }

  return suggestions;
};

// ── Recent Actions ───────────────────────────────────────────────

const getRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; }
};

const pushRecent = (actionId) => {
  try {
    let recent = getRecent().filter(id => id !== actionId);
    recent.unshift(actionId);
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch {}
};

// ── Fuzzy Match (scored) ─────────────────────────────────────────

const fuzzyMatch = (query, text) => {
  if (!text) return false;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  if (t.includes(q)) return true;
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length;
};

const fuzzyScore = (query, text) => {
  if (!text) return 0;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  if (t.includes(q)) return 60;
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length ? 30 : 0;
};

// ── Thread Search ────────────────────────────────────────────────

const searchThreads = (threads, query, isAdmin, userId) => {
  if (!query || query.length < 2 || !threads.length) return [];
  const scored = [];
  for (const t of threads) {
    if (!isAdmin && t.assigned_agent_id && t.assigned_agent_id !== userId) continue;
    const patientName = t.participants?.find(p => p.role === 'patient')?.name || '';
    const coordinatorName = t.assigned_agent_name || '';
    const subject = t.subject || '';
    const best = Math.max(fuzzyScore(query, patientName), fuzzyScore(query, subject), fuzzyScore(query, coordinatorName));
    if (best > 0) {
      scored.push({
        id: `lead-${t.id}`, threadId: t.id,
        label: patientName || subject || t.id.slice(0, 8),
        sublabel: subject && patientName ? subject : undefined,
        status: t.lead_status, source: t.lead_source,
        group: 'Leads', icon: UserCircle,
        route: '/clinic/messages', threadNav: true, score: best,
      });
    }
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, MAX_LEAD_RESULTS);
};

// ── Component ────────────────────────────────────────────────────

export const CommandPalette = ({ isAdmin = false, userId, clinicId, onPresetChange, onTabChange, analyticsSummary, preloadedThreads }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [threads, setThreads] = useState([]);
  const [threadsFetched, setThreadsFetched] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  const actions = useMemo(() => buildActions({ isAdmin }), [isAdmin]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Use preloaded threads if available, otherwise fetch on first open
  useEffect(() => {
    if (preloadedThreads && preloadedThreads.length > 0) {
      setThreads(preloadedThreads);
      setThreadsFetched(true);
    }
  }, [preloadedThreads]);

  useEffect(() => {
    if (!open || threadsFetched || !clinicId) return;
    (async () => {
      try {
        const _t = '';
        const res = await api.get(`/v2/threads/clinic/${clinicId}?limit=200&offset=0`, {
          headers: {}
        });
        {
          const data = res.data;
          setThreads(data.threads || []);
        }
      } catch (err) { console.error('CommandPalette thread fetch:', err); }
      finally { setThreadsFetched(true); }
    })();
  }, [open, threadsFetched, clinicId]);

  const recentIds = useMemo(() => open ? getRecent() : [], [open]);

  const leadResults = useMemo(
    () => searchThreads(threads, debouncedQuery, isAdmin, userId),
    [threads, debouncedQuery, isAdmin, userId]
  );

  // Smart suggestions — computed from cached threads + analytics summary
  const suggestions = useMemo(
    () => (open && !debouncedQuery.trim()) ? buildSuggestions(threads, analyticsSummary, isAdmin, userId) : [],
    [open, debouncedQuery, threads, analyticsSummary, isAdmin, userId]
  );

  const filtered = useMemo(() => {
    if (!debouncedQuery.trim()) {
      const recentActions = recentIds.map(id => actions.find(a => a.id === id)).filter(Boolean);
      const rest = actions.filter(a => !recentIds.includes(a.id));
      return { suggestions, recent: recentActions, results: rest, leads: [] };
    }
    const matched = actions.filter(a => fuzzyMatch(debouncedQuery, a.label) || fuzzyMatch(debouncedQuery, a.group));
    return { suggestions: [], recent: [], results: matched, leads: leadResults };
  }, [debouncedQuery, actions, recentIds, leadResults, suggestions]);

  const allItems = useMemo(
    () => [...filtered.suggestions, ...filtered.recent, ...filtered.results, ...filtered.leads],
    [filtered]
  );

  // Keyboard: Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if (isMobile()) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery(''); setDebouncedQuery(''); setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => { setSelectedIdx(0); }, [debouncedQuery]);

  const execute = useCallback((action) => {
    if (!action) return;
    pushRecent(action.id);
    setOpen(false);

    if (action.preset && onPresetChange) { onPresetChange(action.preset); return; }
    if (action.tab && onTabChange) { onTabChange(action.tab); }
    if (action.threadNav && action.threadId) { navigate(`/clinic/messages?thread=${action.threadId}`); return; }
    if (action.route) { navigate(action.route); }
  }, [navigate, onPresetChange, onTabChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(prev => Math.min(prev + 1, allItems.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(prev => Math.max(prev - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); execute(allItems[selectedIdx]); }
  }, [allItems, selectedIdx, execute]);

  useEffect(() => {
    if (listRef.current) {
      const el = listRef.current.querySelector(`[data-idx="${selectedIdx}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIdx]);

  if (!open) return null;

  // Group action results
  const groupedActions = {};
  filtered.results.forEach(a => {
    if (!groupedActions[a.group]) groupedActions[a.group] = [];
    groupedActions[a.group].push(a);
  });

  let flatIndex = filtered.suggestions.length + filtered.recent.length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]" onClick={() => setOpen(false)} data-testid="command-palette-overlay">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150" onClick={(e) => e.stopPropagation()} data-testid="command-palette">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <Search className="h-4 w-4 text-slate-400 shrink-0" />
          <input
            ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown} placeholder="Type a command or search leads..."
            className="flex-1 text-sm text-slate-800 placeholder-slate-400 outline-none bg-transparent"
            data-testid="command-palette-input"
          />
          <kbd className="text-[10px] text-slate-400 bg-slate-100 rounded px-1.5 py-0.5 font-mono">Esc</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[360px] overflow-y-auto py-1" data-testid="command-palette-results">
          {allItems.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-400" data-testid="command-palette-empty">No results for &ldquo;{query}&rdquo;</div>
          )}

          {/* Smart Suggestions */}
          {filtered.suggestions.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                <Zap className="h-2.5 w-2.5 text-amber-500" /> Suggestions
              </div>
              {filtered.suggestions.map((s, i) => (
                <SuggestionItem key={s.id} suggestion={s} selected={selectedIdx === i}
                  onExecute={() => execute(s)} onHover={() => setSelectedIdx(i)}
                  idx={i} testId={`cmd-${s.id}`} />
              ))}
            </>
          )}

          {/* Recent */}
          {filtered.recent.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                <Clock className="h-2.5 w-2.5" /> Recent
              </div>
              {filtered.recent.map((action, i) => {
                const idx = filtered.suggestions.length + i;
                return (
                  <ActionItem key={action.id} action={action} selected={selectedIdx === idx}
                    onExecute={() => execute(action)} onHover={() => setSelectedIdx(idx)}
                    idx={idx} testId={`cmd-recent-${action.id}`} />
                );
              })}
            </>
          )}

          {/* Grouped action sections */}
          {Object.entries(groupedActions).map(([group, items]) => {
            const sectionStart = flatIndex;
            flatIndex += items.length;
            return (
              <React.Fragment key={group}>
                <div className="px-3 pt-2.5 pb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{group}</div>
                {items.map((action, j) => {
                  const idx = sectionStart + j;
                  return (
                    <ActionItem key={action.id} action={action} selected={selectedIdx === idx}
                      onExecute={() => execute(action)} onHover={() => setSelectedIdx(idx)}
                      idx={idx} testId={`cmd-${action.id}`} />
                  );
                })}
              </React.Fragment>
            );
          })}

          {/* Lead results */}
          {filtered.leads.length > 0 && (
            <>
              <div className="px-3 pt-2.5 pb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                <Users className="h-2.5 w-2.5" /> Leads ({filtered.leads.length})
              </div>
              {filtered.leads.map((lead, j) => {
                const idx = flatIndex + j;
                return (
                  <LeadItem key={lead.id} lead={lead} selected={selectedIdx === idx}
                    onExecute={() => execute(lead)} onHover={() => setSelectedIdx(idx)}
                    idx={idx} testId={`cmd-${lead.id}`} />
                );
              })}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-slate-100 flex items-center gap-4 text-[10px] text-slate-400">
          <span className="flex items-center gap-1"><kbd className="bg-slate-100 rounded px-1 py-px font-mono">&#8593;&#8595;</kbd> Navigate</span>
          <span className="flex items-center gap-1"><kbd className="bg-slate-100 rounded px-1 py-px font-mono">Enter</kbd> Execute</span>
          <span className="flex items-center gap-1"><kbd className="bg-slate-100 rounded px-1 py-px font-mono">Esc</kbd> Close</span>
          <span className="ml-auto flex items-center gap-1"><Command className="h-2.5 w-2.5" />+K</span>
        </div>
      </div>
    </div>
  );
};

// ── Suggestion Item Row ──────────────────────────────────────────

const SuggestionItem = ({ suggestion, selected, onExecute, onHover, idx, testId }) => {
  const Icon = suggestion.icon;
  return (
    <button
      onClick={onExecute} onMouseEnter={onHover} data-idx={idx}
      className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${selected ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
      data-testid={testId}
    >
      <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 border ${suggestion.bg}`}>
        <Icon className={`h-3.5 w-3.5 ${suggestion.color}`} />
      </div>
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className={`text-sm ${selected ? 'text-slate-900 font-medium' : 'text-slate-700'}`}>{suggestion.label}</span>
        <span className={`text-xs font-bold ${suggestion.color}`} data-testid={`${testId}-count`}>{suggestion.count}</span>
      </div>
      {selected && <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
    </button>
  );
};

// ── Action Item Row ──────────────────────────────────────────────

const ActionItem = ({ action, selected, onExecute, onHover, idx, testId }) => {
  const Icon = action.icon;
  return (
    <button
      onClick={onExecute} onMouseEnter={onHover} data-idx={idx}
      className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${selected ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
      data-testid={testId}
    >
      <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${selected ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500'}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <span className={`text-sm flex-1 min-w-0 truncate ${selected ? 'text-slate-900 font-medium' : 'text-slate-700'}`}>{action.label}</span>
      {selected && <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
    </button>
  );
};

// ── Lead Item Row ────────────────────────────────────────────────

const STATUS_COLORS = {
  NEW: 'bg-slate-500', CONTACTED: 'bg-sky-500', QUALIFIED: 'bg-indigo-500',
  OFFER_SENT: 'bg-violet-500', NEGOTIATION: 'bg-amber-500', WON: 'bg-emerald-500', LOST: 'bg-red-500',
};

const LeadItem = ({ lead, selected, onExecute, onHover, idx, testId }) => (
  <button
    onClick={onExecute} onMouseEnter={onHover} data-idx={idx}
    className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${selected ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
    data-testid={testId}
  >
    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold ${selected ? 'bg-sky-600 text-white' : 'bg-sky-100 text-sky-700'}`}>
      {lead.label?.charAt(0)?.toUpperCase() || '?'}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5">
        <span className={`text-sm truncate ${selected ? 'text-slate-900 font-medium' : 'text-slate-700'}`}>{lead.label}</span>
        {lead.status && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_COLORS[lead.status] || 'bg-slate-400'}`} title={lead.status} />}
      </div>
      {lead.sublabel && <span className="text-[11px] text-slate-400 truncate block">{lead.sublabel}</span>}
    </div>
    {selected && <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
  </button>
);

export default CommandPalette;
